/**
 * MEETING MAPPERS — conversione tra tipi UI e DTO REST.
 *
 * Solo `status` viaggia come int32 (allineato a MeetingStatus.java).
 * `duration` e' int (minuti) — passa cosi' com'e'.
 *
 * SYNC CROSS-LAYER — vedi docblock in meetingTypes.ts.
 *   PLANNED=0, REPLANNED=1, MISSING=2, CANCELLED=3, COMPLETED=4
 */

import { enumFromDto, pickAuditFields } from '@crm/shared';
import type { Meeting, MeetingCreatePayload, MeetingStatus, MeetingUpdatePayload } from './meetingTypes';

// SYNC with MeetingStatus.java
const MEETING_STATUS_TO_INT: Record<MeetingStatus, number> = {
  planned: 0,
  replanned: 1,
  missing: 2,
  cancelled: 3,
  completed: 4,
};
const MEETING_STATUS_FROM_INT: Record<number, MeetingStatus> = invert(MEETING_STATUS_TO_INT);

const meetingStatusToDto = (v: MeetingStatus): number => MEETING_STATUS_TO_INT[v];
const meetingStatusFromDto = (v: number): MeetingStatus =>
  enumFromDto('MeetingStatus', v, MEETING_STATUS_FROM_INT, 'planned');

export interface MeetingDto {
  meetingId: number;
  companyId: number;
  groupId: number;
  userId: number;
  userName: string;
  createDate: string;
  modifiedDate: string;
  meetingDate: string;
  duration: number;
  title?: string;
  description: string;
  location?: string;
  notes?: string;
  status: number;
}

export function meetingFromDto(dto: MeetingDto): Meeting {
  return {
    meetingId: dto.meetingId,
    ...pickAuditFields(dto),
    meetingDate: dto.meetingDate,
    duration: dto.duration,
    title: dto.title,
    description: dto.description,
    location: dto.location,
    notes: dto.notes,
    status: meetingStatusFromDto(dto.status),
  };
}

export function meetingToDto(m: Meeting): MeetingDto {
  return {
    meetingId: m.meetingId,
    ...pickAuditFields(m),
    meetingDate: m.meetingDate,
    duration: m.duration,
    title: m.title,
    description: m.description,
    location: m.location,
    notes: m.notes,
    status: meetingStatusToDto(m.status),
  };
}

export interface MeetingInput {
  meetingDate?: string;
  duration?: number;
  description?: string;
  status?: number;
}

export function meetingCreateToInput(p: MeetingCreatePayload): MeetingInput {
  return {
    meetingDate: p.meetingDate,
    duration: p.duration,
    description: p.description,
    status: meetingStatusToDto(p.status),
  };
}

export function meetingUpdateToInput(p: Omit<MeetingUpdatePayload, 'meetingId'>): MeetingInput {
  const out: MeetingInput = {};
  if (p.meetingDate !== undefined) out.meetingDate = p.meetingDate;
  if (p.duration !== undefined) out.duration = p.duration;
  if (p.description !== undefined) out.description = p.description;
  if (p.status !== undefined) out.status = meetingStatusToDto(p.status);
  return out;
}

export function meetingInputFromDto(input: MeetingInput): Partial<Meeting> {
  const out: Partial<Meeting> = {};
  if (input.meetingDate !== undefined) out.meetingDate = input.meetingDate;
  if (input.duration !== undefined) out.duration = input.duration;
  if (input.description !== undefined) out.description = input.description;
  if (input.status !== undefined) out.status = meetingStatusFromDto(input.status);
  return out;
}

function invert<K extends string>(src: Record<K, number>): Record<number, K> {
  const out = {} as Record<number, K>;
  for (const key of Object.keys(src) as K[]) out[src[key]] = key;
  return out;
}
